// 後台「營運分析」—— ①營收/MRR ②轉換漏斗 ③留存/流失（Phase 3）
//   全自家資料（paymenttransaction / subscriptionmandate / user / auditlog），無外部套件。
import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { Box } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "AuthContext";
import { COLORS } from "theme/tokens";

const fmtMoney = (n) => "NT$" + Number(n || 0).toLocaleString();

function StatTile({ label, value, sub }) {
  return (
    <Card sx={{ p: 2.5, textAlign: "center", height: "100%" }}>
      <MDTypography variant="h4" fontWeight="bold" color="info">{value}</MDTypography>
      <MDTypography variant="button" color="text">{label}</MDTypography>
      {sub ? <MDTypography variant="caption" color="text" sx={{ display: "block" }}>{sub}</MDTypography> : null}
    </Card>
  );
}

function RankList({ items, fmt }) {
  const max = items.reduce((m, it) => Math.max(m, it.count), 0) || 1;
  if (!items.length) return <MDTypography variant="button" color="text">無資料</MDTypography>;
  return (
    <Box display="flex" flexDirection="column" gap={0.9}>
      {items.map((it, i) => (
        <Box key={i}>
          <Box display="flex" justifyContent="space-between" sx={{ fontSize: 12.5 }}>
            <Box>{it.label}</Box>
            <Box sx={{ fontWeight: 600, color: COLORS.grayDark }}>{fmt ? fmt(it.count) : it.count}</Box>
          </Box>
          <Box sx={{ height: 4, borderRadius: 2, bgcolor: "#eef0f3", mt: "3px" }}>
            <Box sx={{ height: 4, borderRadius: 2, width: `${Math.round((it.count / max) * 100)}%`, bgcolor: "#ff8a3d" }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function MonthlyBars({ series }) {
  const max = series.reduce((m, s) => Math.max(m, s.total), 0) || 1;
  if (!series.length) return <MDTypography variant="button" color="text">無資料</MDTypography>;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1, minHeight: 150, overflowX: "auto", pt: 2 }}>
      {series.map((s) => (
        <Box key={s.month} sx={{ flex: "1 0 34px", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Box sx={{ fontSize: 9, color: COLORS.grayDark, whiteSpace: "nowrap" }}>{s.total ? Number(s.total).toLocaleString() : ""}</Box>
          <Box
            title={`${s.month}：${fmtMoney(s.total)}`}
            sx={{ width: "68%", height: `${Math.round((s.total / max) * 110)}px`, minHeight: s.total ? 2 : 0, bgcolor: "#ff8a3d", borderRadius: "3px 3px 0 0", mt: "2px" }}
          />
          <Box sx={{ fontSize: 9, color: COLORS.grayDark, mt: 0.5 }}>{s.month.slice(2)}</Box>
        </Box>
      ))}
    </Box>
  );
}

export default function OpsAnalytics() {
  const { token } = useAuth();
  const [rev, setRev] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [ret, setRet] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [rRes, fRes, xRes] = await Promise.all([
        fetch(`/api/admin/ops-analytics/revenue`, { headers }),
        fetch(`/api/admin/ops-analytics/funnel`, { headers }),
        fetch(`/api/admin/ops-analytics/retention`, { headers }),
      ]);
      const rD = await rRes.json();
      setRev(rRes.ok ? rD : null);
      const fD = await fRes.json();
      setFunnel(fRes.ok ? fD : null);
      const xD = await xRes.json();
      setRet(xRes.ok ? xD : null);
    } catch (e) {
      setRev(null); setFunnel(null); setRet(null);
      console.error("fetch ops analytics error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const t = rev?.totals || {};

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <MDTypography variant="h5" fontWeight="bold">營運分析</MDTypography>
            <MDButton color="info" variant="gradient" size="small" onClick={fetchAll} disabled={loading}>
              {loading ? "讀取中..." : "重新整理"}
            </MDButton>
          </Box>
        </Card>

        {/* ① 營收 / MRR */}
        <MDTypography variant="h6" fontWeight="bold" mb={1}>營收 / MRR</MDTypography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}><StatTile label="MRR（月經常性收入）" value={fmtMoney(t.mrr)} sub={`活躍訂閱 ${t.activeSubs ?? 0}`} /></Grid>
          <Grid item xs={12} md={3}><StatTile label="ARPU（每訂閱月均）" value={fmtMoney(t.arpu)} /></Grid>
          <Grid item xs={12} md={3}><StatTile label="總營收（累計）" value={fmtMoney(t.totalRevenue)} /></Grid>
          <Grid item xs={12} md={3}><StatTile label="付款失敗 / 退款" value={`${t.failedCount ?? 0} / ${t.refundedCount ?? 0}`} /></Grid>
        </Grid>

        <Grid container spacing={3} mt={0.5}>
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 2, height: "100%" }}>
              <MDTypography variant="h6" fontWeight="bold">近 12 月營收趨勢</MDTypography>
              <MDTypography variant="caption" color="text">
                方案 {fmtMoney(t.planRevenue)} · Coin {fmtMoney(t.coinRevenue)} · 租賃 {fmtMoney(t.rentalRevenue)}
              </MDTypography>
              <MonthlyBars series={rev?.monthlySeries || []} />
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2, height: "100%" }}>
              <MDTypography variant="h6" fontWeight="bold" mb={1.5}>各方案營收（累計）</MDTypography>
              <RankList items={(rev?.planBreakdown || []).map((p) => ({ label: p.planCode, count: p.amount }))} fmt={fmtMoney} />
            </Card>
          </Grid>
        </Grid>

        {/* ② 轉換漏斗 */}
        <MDTypography variant="h6" fontWeight="bold" mt={4} mb={1}>轉換漏斗</MDTypography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ p: 2, height: "100%" }}>
              {(funnel?.stages || []).map((s, i) => (
                <Box key={s.key} mb={1.2}>
                  <Box display="flex" justifyContent="space-between" sx={{ fontSize: 13 }}>
                    <Box>
                      {i + 1}. {s.label}
                      {i > 0 ? <Box component="span" sx={{ color: COLORS.grayDark, ml: 1 }}>轉換 {s.pctOfPrev}%</Box> : null}
                    </Box>
                    <Box sx={{ fontWeight: 600 }}>{s.count}（{s.pctOfTop}%）</Box>
                  </Box>
                  <Box sx={{ height: 10, borderRadius: 2, bgcolor: "#eef0f3", mt: "3px" }}>
                    <Box sx={{ height: 10, borderRadius: 2, width: `${s.pctOfTop}%`, bgcolor: "#ff8a3d" }} />
                  </Box>
                </Box>
              ))}
              {!funnel?.stages?.length ? <MDTypography variant="button" color="text">無資料</MDTypography> : null}
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <StatTile label="試用轉付費率" value={`${funnel?.trialToPaidPct ?? 0}%`} sub="曾付費 ÷ 已驗證" />
          </Grid>
        </Grid>

        {/* ③ 留存 / 流失 */}
        <MDTypography variant="h6" fontWeight="bold" mt={4} mb={1}>留存 / 流失</MDTypography>
        <Grid container spacing={3}>
          <Grid item xs={6} md={3}><StatTile label="近 7 天活躍" value={ret?.totals?.active7 ?? 0} /></Grid>
          <Grid item xs={6} md={3}><StatTile label="近 30 天活躍" value={ret?.totals?.active30 ?? 0} sub={`沉睡 ${ret?.totals?.dormant ?? 0}`} /></Grid>
          <Grid item xs={6} md={3}><StatTile label="付費會員" value={ret?.totals?.activePaid ?? 0} sub={`排定取消 ${ret?.totals?.autoRenewOff ?? 0}`} /></Grid>
          <Grid item xs={6} md={3}><StatTile label="已停 / 取消訂閱" value={ret?.totals?.stoppedMandates ?? 0} /></Grid>
        </Grid>
        <Grid container spacing={3} mt={0.5}>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDTypography variant="button" color="text">
                自動降級累計：試用過期 {ret?.totals?.trialDowngrades ?? 0} · 付費到期 {ret?.totals?.memberDowngrades ?? 0}
              </MDTypography>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
